import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsis,
    faPenToSquare,
    faCircleMinus,
    faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";

import { getAllCustomers, deleteCustomer } from "../../api/Customers";
import { getCustomerDisplayName, getOrganizationDisplayName } from "../../helpers/CustomerDisplay";
import { useAuth, Roles } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import DataTable from "../../components/DataTable";
import TdLink from "../../components/TdLink";
import { useTranslation } from "react-i18next";

const CustomersPage = () => {
    const { t } = useTranslation();
    const { getAccessToken, hasRole } = useAuth();
    const navigate = useNavigate();

    const [customers, setCustomers] = useState([]);

    const [search, setSearch] = useState("");
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: "",
        description: "",
        confirm: () => { },
    });
    const [customersLoaded, setCustomersLoaded] = useState(false);

    useEffect(() => {
        handleGetCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, []);

    const handleGetCustomers = async () => {
        try {
            const accessToken = await getAccessToken();
            const newCustomers = await getAllCustomers(accessToken);

            setCustomers(newCustomers);
            setCustomersLoaded(true);
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeleteCustomer = async (customerPublicId) => {
        const toastId = toast.loading(t("toast.deleting", { entity: t("entity.customer") }));
        try {
            const accessToken = await getAccessToken();
            await deleteCustomer(customerPublicId, accessToken);

            await handleGetCustomers();

            toast.success(t("toast.deleted", { entity: t("entity.customer") }), { id: toastId });
        } catch (error) {
            toast.error(t("toast.failedDelete", { entity: t("entity.customer") }), { id: toastId });
            console.error(error);
        }
    };

    const columns = [
        {
            key: "customerPublicId",
            header: t("table.customerId"),
            sortable: true,
            rawCell: true,
            getSortValue: (customer) => customer.customerPublicId ?? "",
            render: (customer) => (
                <TdLink to={`/customer/${customer.customerPublicId}`}>
                    {customer.customerPublicId || ""}
                </TdLink>
            ),
        },
        {
            key: "name",
            header: t("common.name"),
            sortable: true,
            rawCell: true,
            getSortValue: (customer) => getCustomerDisplayName(customer) ?? "",
            render: (customer) => (
                <TdLink to={`/customer/${customer.customerPublicId}`}>
                    {getCustomerDisplayName(customer)}
                </TdLink>
            ),
        },
        {
            key: "organizationName",
            header: t("entity.organization"),
            sortable: true,
            rawCell: true,
            hidden: true,
            getSortValue: (customer) => getOrganizationDisplayName(customer),
            render: (customer) => (
                <TdLink to={`/customer/${customer.customerPublicId}`}>
                    {getOrganizationDisplayName(customer) || "-"}
                </TdLink>
            ),
        },
        {
            key: "paymentMethodName",
            header: t("filter.Payment method"),
            sortable: true,
            rawCell: true,
            getSortValue: (customer) => customer.paymentMethod?.paymentMethodName ?? "",
            render: (customer) => (
                <TdLink to={`/customer/${customer.customerPublicId}`}>
                    {customer.paymentMethod?.paymentMethodName || ""}
                </TdLink>
            ),
        },
        {
            key: "actions",
            header: "",
            sortable: false,
            searchable: false,
            rawCell: true,
            render: (customer) => (
                <td className="text-center">
                    <div className="flex justify-end">
                        <CustomDropdown
                            isRight={true}
                            ContentComponent={() => (
                                <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                    <button
                                        className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                        onClick={() => navigate(`/customer/${customer.customerPublicId}`)}
                                    >
                                        <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                                    </button>
                                    <button
                                        className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                        onClick={() =>
                                            setConfirmState({
                                                isOpen: true,
                                                title: t("confirm.deleteEntity", {
                                                    entity: t("entity.customer"),
                                                    name: getCustomerDisplayName(customer),
                                                }),
                                                description: t("confirm.deleteCustomerDescription"),
                                                confirm: () => handleDeleteCustomer(customer.customerPublicId),
                                            })
                                        }
                                    >
                                        <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                    </button>
                                </div>
                            )}
                        >
                            <div className="w-8 h-6 flex justify-end items-center cursor-pointer">
                                <FontAwesomeIcon icon={faEllipsis} />
                            </div>
                        </CustomDropdown>
                    </div>
                </td>
            ),
        },
    ];

    return (
        <>
            <HeaderBar />
            <div className="flex flex-col">
                <div className="flex flex-row">
                    <Sidebar />
                    <main className="page">
                        <div className="absolute inset-0 pointer-events-none">
                            <LoadingTablePage title={t("entity.customers")} loading={!customersLoaded} />
                        </div>

                        <div className={`${!customersLoaded ? "loading" : ""} page-content`}>
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl text-black font-bold pb-2">{t("entity.customers")}</h2>
                                <button className="btn btn-primary" onClick={() => navigate(`/customer/create`)}>
                                    <FontAwesomeIcon icon={faPlus} /> {t("pages.createCustomer")}
                                </button>
                            </div>

              <div className="rounded-md shadow-md w-full p-4">
                                <DataTable
                                    rows={customers}
                                    columns={columns}
                                    search={search}
                                    onSearchChange={setSearch}
                                    searchPlaceholder={t("search.customer")}
                                    rowKey={(customer, index) => customer.customerPublicId || index}
                                    emptyColSpan={hasRole(Roles.Admin) ? 5 : 4}
                                />
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <ConfirmationModal
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                description={confirmState.description}
                onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
                onConfirm={() => {
                    setConfirmState((s) => ({ ...s, isOpen: false }));
                    confirmState.confirm();
                }}
            />
        </>
    );
};

export default CustomersPage;
